import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { assetUrl } from "@/utils/assetUrl";
import {
  getProductById,
  getProducts,
  getProductCategories,
  getArchitecture,
  getBrandWebsites,
  type Product,
  type L1Category,
  type L3Category,
  type ArchitectureInfo,
} from "@/api/productApi";
import { useSEO, buildBreadcrumbLd, SITE_ORIGIN } from "@/utils/seo";
import { readPreload, buildPreloadKey } from "@/api/preload";
import { CATEGORY_ACCENT_COLOR, CATEGORY_NAV_DISPLAY } from "@/utils/solutionDisplay";
import FaqBlock from "@/components/feature/FaqBlock";

const productTypeLabels: Record<string, string> = {
  hardware: "硬件",
  software: "软件",
  service: "服务",
};


// ── Brand Logo 组件：通过品牌官网域名获取真实 favicon/logo ──────────────────
function BrandLogo({ name, accentColor, website }: { name: string; accentColor: string; website: string }) {
  const [imgError, setImgError] = useState(false);

  const getDomain = (url: string) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return "";
    }
  };

  const domain = getDomain(website);
  // 使用 Google Favicon 服务获取高清品牌 logo（128px）
  const logoUrl = domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
    : "";

  // 首字母 fallback
  const initials = name
    .split(/[\s/·]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  if (!domain || imgError) {
    return (
      <div
        className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 text-sm font-bold text-white"
        style={{ backgroundColor: accentColor }}
      >
        {initials}
      </div>
    );
  }

  return (
    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 bg-white border border-gray-100 overflow-hidden">
      <img
        src={assetUrl(logoUrl)}
        alt={name}
        className="w-7 h-7 object-contain"
        onError={() => setImgError(true)}
      />
    </div>
  );
}


const ALL_TABS = [
  { id: "overview", label: "产品概述" },
  { id: "features", label: "功能特性" },
  { id: "advantages", label: "产品亮点" },
  { id: "specs", label: "选型标准" },
  { id: "variants", label: "型号版本" },
];

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showBackBtn, setShowBackBtn] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  // 点击 tab 滚动期间，暂停 scroll-spy 对 activeTab 的自动覆盖
  const scrollLockRef = useRef(false);

  // API data
  const [product, setProduct] = useState<Product | null>(
    () =>
      (id
        ? readPreload<Product>(buildPreloadKey("GET", "/api/front/products/" + Number(id)))
        : undefined) ?? null,
  );
  const [l1Categories, setL1Categories] = useState<L1Category[]>([]);
  const [l3Categories, setL3Categories] = useState<L3Category[]>([]);
  const [archInfo, setArchInfo] = useState<ArchitectureInfo | null>(null);
  const [brandWebsites, setBrandWebsites] = useState<Record<string, string>>({});
  const [related, setRelated] = useState<Product[]>([]);
  // 首帧命中内嵌预载数据时（product !== null）直接跳过加载骨架屏：预渲染产物
  // 落盘时 product 已加载完成、loading 已为 false，若客户端首帧仍固定从
  // loading=true 起步，会在水合时与 Raw_HTML 的实际内容产生不一致（需求 11.13）
  const [loading, setLoading] = useState(() => product === null);
  const [error, setError] = useState<string | null>(null);

  useSEO(
    product
      ? {
          title: `${product.name}${product.model ? ` ${product.model}` : ""} | 产品详情`,
          description:
            (product.desc || "").slice(0, 160) ||
            `欧格登${product.name}产品详情，了解功能特性、规格参数与适用场景。`,
          keywords: [
            product.name,
            product.model,
            product.tag,
            ...(product.scenarioTags || []),
            ...(product.useCases || []),
          ].filter(Boolean) as string[],
          path: `/products/${id}`,
          pageType: "PRODUCT_DETAIL",
          image: (product as any).image
            ? ((product as any).image.startsWith("http")
                ? (product as any).image
                : `${SITE_ORIGIN}${(product as any).image}`)
            : undefined,
          ogType: "product",
          jsonLd: [
            buildBreadcrumbLd([
              { name: "首页", path: "/" },
              { name: "产品中心", path: "/products" },
              { name: product.name, path: `/products/${id}` },
            ]),
            {
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              description: product.desc || "",
              sku: product.model || String(product.id),
              category: product.category || "",
              brand: {
                "@type": "Brand",
                name: (product as any).brand || "欧格登",
              },
            },
          ],
        }
      : {
          title: "产品详情",
          description: "欧格登产品详情页",
          path: `/products/${id ?? ""}`,
          pageType: "PRODUCT_DETAIL",
        },
  );

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Load product data
  useEffect(() => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    Promise.all([
      getProductById(Number(id)),
      getProductCategories(),
      getBrandWebsites(),
    ])
      .then(([prod, cats, brands]) => {
        setProduct(prod);
        setL1Categories(cats.l1Categories || []);
        setL3Categories(cats.l3Categories || []);
        setBrandWebsites(brands || {});
        // Load architecture info
        if (prod?.category) {
          getArchitecture(prod.category)
            .then((arch) => setArchInfo(arch))
            .catch(() => setArchInfo({ title: "产品部署架构", image: "", desc: "产品采用标准化分层架构设计。" }));
        }
        // Load related products
        if (prod?.category) {
          getProducts({ category: prod.category })
            .then((prods) => {
              setRelated((prods || []).filter((p: Product) => p.id !== prod.id).slice(0, 3));
            })
            .catch(() => setRelated([]));
        }
      })
      .catch(() => {
        setError("加载产品详情失败，请稍后重试");
        setProduct(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Listen scroll: show back button after scrolling past hero section + scroll-spy 高亮当前版块
  useEffect(() => {
    const sectionIds = ["overview", "features", "advantages", "specs", "variants"];
    const handleScroll = () => {
      const heroHeight = heroRef.current?.offsetHeight ?? 400;
      const scrolled = window.scrollY;
      setShowBackBtn(scrolled > heroHeight - 72);
      setShowBackToTop(scrolled > 300);

      // scroll-spy：找出当前视口顶部偏移内最靠上的版块
      if (!scrollLockRef.current) {
        const offset = 180;
        let current = "";
        for (const sid of sectionIds) {
          const el = document.getElementById(`psection-${sid}`);
          if (el && el.getBoundingClientRect().top <= offset) {
            current = sid;
          }
        }
        if (current) setActiveTab(current);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [product]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-white">
        <div className="w-full h-[420px] bg-[#0d1f3c] animate-pulse" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 space-y-4">
              <div className="h-6 bg-gray-100 rounded w-1/4 animate-pulse" />
              <div className="h-8 bg-gray-100 rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-full animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-5/6 animate-pulse" />
              <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
            </div>
            <div className="w-full lg:w-72 space-y-4">
              <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
              <div className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="text-center mt-32">
          <i className="ri-wifi-off-line text-5xl text-gray-300 mb-4 block"></i>
          <h2 className="text-xl font-bold text-gray-600 mb-2">{error ? "加载失败" : "产品不存在"}</h2>
          <p className="text-gray-400 text-sm mb-6">{error || "未找到该产品信息"}</p>
          <div className="flex gap-3 justify-center">
            {error && (
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2.5 bg-[#1a56db] text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap"
              >
                重新加载
              </button>
            )}
            <Link to="/products" className="px-6 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-full whitespace-nowrap">
              返回产品列表
            </Link>
          </div>
        </div>
      </main>
    );
  }

  // Derive display data from loaded product + categories
  const l3info = l3Categories.find((c) => c.id === product.category);
  const l1info = l1Categories.find((c) => c.id === product.category);
  const solutionId = l3info?.l1 ?? l1info?.id ?? product.relatedSolution ?? product.category ?? "info-security";
  const accentColor = CATEGORY_ACCENT_COLOR[solutionId] ?? CATEGORY_ACCENT_COLOR[product.category] ?? "#1a56db";
  const solutionName = CATEGORY_NAV_DISPLAY[solutionId]?.shortName ?? l1info?.name ?? "";
  const catName = l3info?.name ?? product.tag ?? l1info?.name ?? "";
  const types = Array.isArray(product.type) ? product.type : (product.type ? [product.type] : []);

  // 动态生成 tab：仅保留有内容的版块
  const hasVariants = ((product as any).versions?.length ?? 0) > 0 || (product.variants?.length ?? 0) > 0;
  const tabs = ALL_TABS.filter((t) => {
    if (t.id === "overview") return true;
    if (t.id === "features") return (product.features?.length ?? 0) > 0;
    if (t.id === "advantages") return (product.advantages?.length ?? 0) > 0;
    if (t.id === "specs") return (product.specs?.length ?? 0) > 0;
    if (t.id === "variants") return hasVariants;
    return false;
  });

  // 点击 tab：滚动到对应版块（带锁，避免滚动动画期间被 scroll-spy 抢回）
  const scrollToSection = (tabId: string) => {
    setActiveTab(tabId);
    scrollLockRef.current = true;
    const el = document.getElementById(`psection-${tabId}`);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top, behavior: "smooth" });
    }
    window.setTimeout(() => { scrollLockRef.current = false; }, 700);
  };

  return (
    <main className="min-h-screen bg-[#f8fafc] overflow-x-clip">
      {/* Hide scrollbar globally for this page */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* Fixed Back Button - Top Left, appears after scrolling past hero */}
      <button
        onClick={() => location.key !== "default" ? navigate(-1) : navigate("/products")}
        className={`fixed left-6 z-50 w-11 h-11 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer ${
          showBackBtn ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        style={{ top: "100px", backgroundColor: accentColor, boxShadow: `0 4px 16px ${accentColor}55` }}
        aria-label="返回产品列表"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>

      {/* Fixed Back to Top Button - Bottom Right, above floating contact */}
      <button
        onClick={scrollToTop}
        className={`fixed right-5 z-50 w-12 h-12 flex items-center justify-center rounded-full text-white transition-all duration-300 cursor-pointer ${
          showBackToTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{ backgroundColor: accentColor, bottom: "96px", boxShadow: `0 4px 16px ${accentColor}55` }}
        aria-label="返回顶部"
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>

      {/* Breadcrumb + Hero */}
      <section ref={heroRef} className="bg-[#0d1f3c] pt-28 pb-0 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={assetUrl(product.image)}
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/80 to-[#0d1f3c]"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <Link to="/products" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">产品中心</Link>
            <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <span className="text-gray-300 whitespace-nowrap">{product.name}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-10 pb-10">
            {/* Left: Info */}
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span
                  className="px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: `${accentColor}25`, color: "#a5c8ff" }}
                >
                  {solutionName}
                </span>
                {types.map((t) => {
                  const tColor = t === "hardware" ? "#0891b2" : t === "software" ? "#1a56db" : "#0e7490";
                  return (
                    <span
                      key={t}
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: `${tColor}25`, color: "#a5c8ff" }}
                    >
                      {productTypeLabels[t]}
                    </span>
                  );
                })}
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-300">
                  {catName}
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2 leading-tight">{product.name}</h1>
              <p className="text-gray-400 text-sm mb-5">{product.model}</p>
              <p className="text-gray-300 text-sm leading-relaxed mb-8 max-w-2xl">{product.desc}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                {(product as any).tags?.map((t: string) => (
                  <div
                    key={t}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: `${accentColor}20`, color: "#c7d9ff" }}
                  >
                    <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xs" style={{ color: accentColor }}></i>
                    </div>
                    {t}
                  </div>
                ))}
              </div>

              {/* CTA buttons */}
              <div className="flex flex-wrap gap-3">
                <a
                  href="/#contact-form"
                  className="px-7 py-3 rounded-full text-sm font-semibold text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: accentColor }}
                >
                  咨询报价
                </a>
                <button
                  onClick={() => navigate("/products")}
                  className="px-7 py-3 rounded-full text-sm font-semibold text-white border border-white/20 hover:bg-white/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  返回列表
                </button>
              </div>
            </div>

            {/* Right: Product image */}
            <div className="w-full lg:w-[420px] flex-shrink-0">
              <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 h-64 lg:h-72">
                <img
                  src={assetUrl(product.image)}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Tab bar — 点击滚动定位，滚动联动高亮 */}
      <div className="sticky top-16 lg:top-20 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex gap-0 overflow-x-auto hide-scrollbar">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => scrollToSection(tab.id)}
                  className="px-5 py-4 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer border-b-2"
                  style={
                    isActive
                      ? { color: accentColor, borderBottomColor: accentColor }
                      : { color: "#6b7280", borderBottomColor: "transparent" }
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>


      {/* Tab Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:items-start">
            {/* Main content */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* Overview */}
              <div id="psection-overview" className="space-y-6 scroll-mt-36">
                  <div className="bg-white rounded-2xl border border-gray-100 p-8">
                    <h2 className="text-lg font-bold text-[#0d1f3c] mb-5">适用场景</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {((product as any).scenes ?? product.useCases ?? []).map((sc: string) => (
                        <div
                          key={sc}
                          className="flex items-center gap-3 p-4 rounded-xl border"
                          style={{ borderColor: `${accentColor}20`, backgroundColor: `${accentColor}04` }}
                        >
                          <div
                            className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                            style={{ backgroundColor: `${accentColor}15` }}
                          >
                            <i className="ri-checkbox-circle-line text-sm" style={{ color: accentColor }}></i>
                          </div>
                          <span className="text-sm font-medium text-[#0d1f3c]">{sc}</span>
                        </div>
                      ))}
                    </div>
                    {(product as any).scenesImage && (
                      <div className="mt-6">
                        <img
                          src={assetUrl((product as any).scenesImage)}
                          alt="适用场景"
                          className="w-full rounded-xl border border-gray-100"
                        />
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 p-8">
                    <h2 className="text-lg font-bold text-[#0d1f3c] mb-5">产品简介</h2>
                    {(product as any).introduction ? (
                      <div
                        className="prose prose-sm max-w-none text-gray-600 leading-relaxed mb-6 break-words"
                        dangerouslySetInnerHTML={{ __html: (product as any).introduction }}
                      />
                    ) : (
                      <p className="text-sm text-gray-600 leading-relaxed mb-6 break-words overflow-hidden">
                        {product.desc}
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {(Array.isArray((product as any).capabilities) ? (product as any).capabilities : []).map((cap: any, i: number) => (
                        <div
                          key={cap.label ?? i}
                          className="p-4 rounded-xl text-center"
                          style={{ backgroundColor: `${accentColor}06` }}
                        >
                          <p className="text-xs text-gray-400 mb-1">{cap.label}</p>
                          <p className="text-sm font-bold text-[#0d1f3c] leading-snug">{cap.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>


              </div>

              {/* Features */}
              {tabs.some((t) => t.id === "features") && (
                <div id="psection-features" className="space-y-6 scroll-mt-36">
                  {/* 功能特性列表 */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-8">
                    <h2 className="text-lg font-bold text-[#0d1f3c] mb-7">功能特性</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {product.features.map((f, i) => (
                        <div key={i} className="flex gap-4">
                          <div
                            className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                            style={{ backgroundColor: `${accentColor}12` }}
                          >
                            <i className={`${f.icon} text-lg`} style={{ color: accentColor }}></i>
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-bold text-[#0d1f3c] mb-1.5">{f.title}</h3>
                            <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Advantages */}
              {tabs.some((t) => t.id === "advantages") && (
                <div id="psection-advantages" className="scroll-mt-36 bg-white rounded-2xl border border-gray-100 p-8">
                  <h2 className="text-lg font-bold text-[#0d1f3c] mb-7">产品亮点</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {product.advantages.map((a, i) => (
                      <div
                        key={i}
                        className="flex flex-col items-center text-center gap-4 p-6 rounded-2xl"
                        style={{ backgroundColor: `${accentColor}06` }}
                      >
                        <div
                          className="w-14 h-14 flex items-center justify-center rounded-2xl"
                          style={{ backgroundColor: `${accentColor}15` }}
                        >
                          <i className={`${a.icon} text-2xl`} style={{ color: accentColor }}></i>
                        </div>
                        <h3 className="text-sm font-bold text-[#0d1f3c]">{a.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{a.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Specs */}
              {tabs.some((t) => t.id === "specs") && (
                <div id="psection-specs" className="scroll-mt-36 bg-white rounded-2xl border border-gray-100 p-8">
                  <h2 className="text-lg font-bold text-[#0d1f3c] mb-7">选型标准</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.specs.map((spec, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 p-4 rounded-xl border"
                        style={{ borderColor: `${accentColor}20`, backgroundColor: `${accentColor}04` }}
                      >
                        <div
                          className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                          style={{ backgroundColor: `${accentColor}15` }}
                        >
                          <i className="ri-checkbox-circle-line text-sm" style={{ color: accentColor }}></i>
                        </div>
                        <span className="text-sm font-medium text-[#0d1f3c]">{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Variants */}
              {tabs.some((t) => t.id === "variants") && (
                <div id="psection-variants" className="space-y-6 scroll-mt-36">
                  {/* Header */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-8">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${accentColor}12` }}>
                        <i className="ri-git-branch-line text-base" style={{ color: accentColor }}></i>
                      </div>
                      <h2 className="text-lg font-bold text-[#0d1f3c]">型号与版本选型</h2>
                    </div>
                    <p className="text-xs text-gray-400 ml-11">根据您的业务规模、性能需求和预算，选择最适合的型号版本</p>
                  </div>

                  {(() => {
                    const versions = (product as any).versions?.length > 0 ? (product as any).versions : product.variants;
                    const versionFeatures = (product as any).versionFeatures ?? [];
                    return (
                      <>
                        {/* Variant cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          {versions.map((v: any, i: number) => (
                            <div
                              key={i}
                              onClick={() => setSelectedVariant(i)}
                              className="relative rounded-2xl border-2 p-6 cursor-pointer transition-all duration-300 flex flex-col"
                              style={selectedVariant === i ? { borderColor: accentColor, backgroundColor: `${accentColor}05` } : { borderColor: "#e5e7eb", backgroundColor: "#fff" }}
                            >
                              {v.recommended && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-white text-xs font-semibold whitespace-nowrap" style={{ backgroundColor: accentColor }}>
                                  {v.badge || "推荐选型"}
                                </div>
                              )}
                              <div className="flex items-start justify-between mb-4">
                                <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: selectedVariant === i ? accentColor : "#f3f4f6" }}>
                                  <i className="ri-check-line text-sm" style={{ color: selectedVariant === i ? "#fff" : "#d1d5db" }}></i>
                                </div>
                                {v.badge && (
                                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: selectedVariant === i ? `${accentColor}12` : "#f3f4f6", color: selectedVariant === i ? accentColor : "#9ca3af" }}>
                                    {v.badge}
                                  </span>
                                )}
                              </div>
                              <h3 className="text-base font-bold text-[#0d1f3c] mb-1">{v.name}</h3>
                              <p className="text-sm font-semibold mb-3" style={{ color: accentColor }}>{v.model}</p>
                              <p className="text-xs text-gray-500 leading-relaxed flex-1">{v.desc ?? v.specs ?? ""}</p>
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="flex items-center gap-1.5 text-xs" style={{ color: accentColor }}>
                                  <i className="ri-information-line text-xs"></i>
                                  <span>{selectedVariant === i ? "当前已选" : "点击选择此型号"}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Comparison table */}
                        {versionFeatures.length > 0 && (
                          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-50 flex items-center gap-3">
                              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: accentColor }}></div>
                              <h3 className="text-sm font-bold text-[#0d1f3c]">型号对比</h3>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr style={{ backgroundColor: `${accentColor}06` }}>
                                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 w-1/4">对比项目</th>
                                    {versions.map((v: any, i: number) => (
                                      <th key={i} className="text-center px-4 py-3.5 text-xs font-semibold" style={{ color: selectedVariant === i ? accentColor : "#6b7280" }}>
                                        <div className="flex flex-col items-center gap-1">
                                          <span>{v.name}</span>
                                          <span className="font-normal text-gray-400">{v.model}</span>
                                        </div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {versionFeatures.map((vf: any, si: number) => (
                                    <tr key={si} className={si % 2 === 0 ? "bg-white" : "bg-gray-50/40"}>
                                      <td className="px-6 py-3.5 text-xs font-medium text-gray-500 border-t border-gray-50">{vf.label}</td>
                                      {versions.map((_: any, vi: number) => (
                                        <td key={vi} className="px-4 py-3.5 text-xs text-center border-t border-gray-50" style={{ color: selectedVariant === vi ? accentColor : "#374151", fontWeight: selectedVariant === vi ? 600 : 400 }}>
                                          {vf.values?.[vi] ?? "-"}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                  <tr className="bg-gray-50/40">
                                    <td className="px-6 py-3.5 text-xs font-medium text-gray-500 border-t border-gray-50">推荐指数</td>
                                    {versions.map((v: any, vi: number) => (
                                      <td key={vi} className="px-4 py-3.5 text-center border-t border-gray-50">
                                        <div className="flex items-center justify-center gap-0.5">
                                          {Array.from({ length: 5 }).map((_, si) => (
                                            <i key={si} className="ri-star-fill text-xs" style={{ color: si < (v.recommended ? 5 : 4) ? (v.recommended ? accentColor : "#f59e0b") : "#e5e7eb" }}></i>
                                          ))}
                                        </div>
                                      </td>
                                    ))}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* CTA bar */}
                  <div
                    className="rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                    style={{ backgroundColor: `${accentColor}08`, border: `1px solid ${accentColor}20` }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                        style={{ backgroundColor: `${accentColor}15` }}
                      >
                        <i className="ri-price-tag-3-line text-xl" style={{ color: accentColor }}></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0d1f3c] mb-0.5">
                          已选：<span style={{ color: accentColor }}>{((product as any).versions?.[selectedVariant] ?? product.variants[selectedVariant])?.name}</span>
                          <span className="text-gray-400 font-normal ml-2">{((product as any).versions?.[selectedVariant] ?? product.variants[selectedVariant])?.model}</span>
                        </p>
                        <p className="text-xs text-gray-500">如需了解详细报价或定制配置，请联系我们的产品顾问</p>
                      </div>
                    </div>
                    <div className="flex gap-3 flex-shrink-0">
                      <a
                        href="/#contact-form"
                        className="px-6 py-2.5 rounded-full text-sm font-semibold text-white whitespace-nowrap cursor-pointer transition-all duration-200"
                        style={{ backgroundColor: accentColor }}
                      >
                        获取报价
                      </a>
                      <a
                        href="/#contact-form"
                        className="px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 border"
                        style={{ color: accentColor, borderColor: `${accentColor}40` }}
                      >
                        申请试用
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Related products */}
          {related.length > 0 && (
            <div className="mt-14">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 rounded-full" style={{ backgroundColor: accentColor }}></div>
                <h2 className="text-base font-bold text-[#0d1f3c]">同类产品推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="w-full h-40 overflow-hidden bg-[#f8fafc] flex-shrink-0">
                      <img
                        src={assetUrl(p.image)}
                        alt={p.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-sm font-bold text-[#0d1f3c] mb-1">{p.name}</h3>
                      <p className="text-xs text-gray-400 mb-2">{p.model}</p>
                      <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 flex-1">{p.desc}</p>
                      <div
                        className="mt-4 text-xs font-semibold flex items-center gap-1"
                        style={{ color: accentColor }}
                      >
                        查看详情
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <FaqBlock scope="PRODUCT_DETAIL" />
    </main>
  );
}
